'use client'
import { FC, useCallback, useMemo } from 'react'
import { useRouter } from '@/i18n/navigation'
import { BsChevronLeft, BsChevronRight } from 'react-icons/bs'
import { formatNumber } from '@/utils/format-number'
import { NAVBAR_HEIGHT } from '@/constants'
import { NavigationListElement } from '@/core/real-estate/typings'

interface NavigatorProps {
  deviceType: 'desktop' | 'mobile'
  currentSlug: string
  navigationList: NavigationListElement[]
  queryString: string
}

/**
 * Renders navigation controls for moving between filtered property results.
 *
 * @param deviceType - Current server-detected device type.
 * @param currentSlug - Slug of the currently displayed property.
 * @param navigationList - Ordered list of related property slugs.
 * @param queryString - Serialized search params used to preserve result context.
 * @returns Sticky navigation controls for back, previous, and next actions.
 */
const Navigator: FC<NavigatorProps> = ({
  deviceType,
  currentSlug,
  navigationList,
  queryString
}) => {
  const isMobile = deviceType === 'mobile'
  const router = useRouter()

  const navigationIndex = useMemo(() => {
    if (navigationList.length > 0) {
      return navigationList.findIndex((_r) => _r.slug.toString() === currentSlug)
    }

    return null
  }, [currentSlug, navigationList])

  const nextId = useMemo(() => {
    if (typeof navigationIndex !== 'number') return null
    const next = navigationIndex + 1
    if (next >= navigationList.length) return null
    return navigationList[next].slug
  }, [navigationIndex, navigationList])

  const prevId = useMemo(() => {
    if (typeof navigationIndex !== 'number') return null
    const prev = navigationIndex - 1
    if (prev < 0) return null
    return navigationList[prev].slug
  }, [navigationIndex, navigationList])

  const handleBack = useCallback(() => {
    router.push(`/${queryString ? '?' + queryString : ''}`)
  }, [queryString, router])

  const handleNavigation = useCallback(
    (slug: string | null) => {
      if (slug === null) return
      router.push(`/propiedades/${slug}${queryString ? '?' + queryString : ''}`)
    },
    [queryString, router]
  )

  const resultsLabel = `Resultados ${navigationList.length ? `(${formatNumber(navigationList.length)})` : ''}`

  return (
    <div
      style={{
        position: 'sticky',
        zIndex: 1100,
        top: NAVBAR_HEIGHT,
        width: '100%',
        background: 'var(--chakra-colors-background-surface)'
      }}>
      <div
        style={{
          width: '100%',
          maxWidth: '80rem',
          marginInline: 'auto',
          padding: '0.5rem 1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
        <button
          type="button"
          onClick={handleBack}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            padding: '0.5rem',
            fontSize: '1rem'
          }}>
          <BsChevronLeft />
          <span>{resultsLabel}</span>
        </button>

        <div style={{ marginInlineStart: 'auto', display: 'flex', gap: '0.5rem' }}>
          <button
            type="button"
            disabled={!prevId}
            onClick={() => handleNavigation(prevId)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              border: 'none',
              background: 'transparent',
              cursor: prevId ? 'pointer' : 'not-allowed',
              padding: '0.5rem',
              fontSize: '1rem',
              opacity: prevId ? 1 : 0.5
            }}>
            <BsChevronLeft />
            {!isMobile ? <span>Anterior</span> : null}
          </button>
          <button
            type="button"
            disabled={!nextId}
            onClick={() => handleNavigation(nextId)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              border: 'none',
              background: 'transparent',
              cursor: nextId ? 'pointer' : 'not-allowed',
              padding: '0.5rem',
              fontSize: '1rem',
              opacity: nextId ? 1 : 0.5
            }}>
            {!isMobile ? <span>Siguiente</span> : null}
            <BsChevronRight />
          </button>
        </div>
      </div>
    </div>
  )
}

export default Navigator
